/**
 * Shared portal visual-viewport CSS contract (safe for Server Components).
 * Live values are written by `usePortalVisualViewport` on the client.
 */

import {
  COMPACT_STATUS_BAR_PX,
  IOS26_VIEWPORT_LIE_SLACK_PX,
  REAL_NOTCH_MIN_PX,
  STANDALONE_SAFE_TOP_FLOOR_PX,
} from "@/lib/virtualspace/standalone-display-mode";

/** Live visual-viewport height for Spatialist portal shells. */
export const PORTAL_VISUAL_HEIGHT_VAR = "--vs-portal-visual-height";

/** Live visual-viewport offsetTop — keeps `position: fixed` shells aligned. */
export const PORTAL_VISUAL_TOP_VAR = "--vs-portal-visual-top";

/**
 * Tailwind/class fragment: fill the synced visual viewport, falling back
 * to `100dvh` before the client sync runs (and in environments without
 * a Visual Viewport API).
 */
export const PORTAL_VISUAL_HEIGHT_CLASS =
  "h-[var(--vs-portal-visual-height,100dvh)]";

/**
 * Fixed shell that tracks the synced visual viewport (Field chrome +
 * mission detail). Prefer this over in-flow `h-full` under the layout
 * frame — mission detail's percentage height was the usual half-screen
 * black gap under the hub after iOS Chrome tab resume.
 */
export const PORTAL_VISUAL_FIXED_SHELL_CLASS =
  "fixed inset-x-0 " +
  "top-[calc(var(--vs-portal-visual-top,0px)+var(--vs-impersonation-banner-offset,0px))] " +
  "h-[calc(var(--vs-portal-visual-height,100dvh)-var(--vs-impersonation-banner-offset,0px))]";

/** Insets owned once by the Spatialist frame, including portaled sheets. */
export const PORTAL_SAFE_TOP_VAR = "--vs-portal-island-top";
export const PORTAL_SAFE_BOTTOM_VAR = "--vs-portal-safe-bottom";
export const PORTAL_ACTIVE_ATTR = "data-vs-spatialist-portal";
/** Same measured top inset, consumed by SpaceStation / SpaceManager / sheets. */
export const DASHBOARD_SAFE_TOP_VAR = "--vs-dashboard-safe-top";

export type PortalViewportMetrics = {
  innerHeight: number;
  layoutHeight: number;
  visualHeight: number;
  visualTop: number;
  scale: number;
  standalone: boolean;
  editableFocused: boolean;
};

/** Hidden 100vh probe — paddingTop is env(), paddingRight is max-inset. */
export const DEVICE_SAFE_INSET_PROBE_STYLE =
  "position:fixed;top:0;left:0;width:0;height:100vh;visibility:hidden;pointer-events:none;box-sizing:border-box;" +
  "padding-top:var(--vs-portal-device-top,env(safe-area-inset-top,0px));" +
  "padding-right:env(safe-area-max-inset-top,0px);" +
  "padding-bottom:var(--vs-portal-device-bottom,env(safe-area-inset-bottom,0px));" +
  "padding-left:var(--vs-portal-device-left,env(safe-area-inset-left,0px))";

/**
 * Installed WebKit can subtract safe areas from visualViewport.height even
 * without a keyboard (WebKit #254868). A measured 100vh owns the app frame;
 * the visual viewport only owns it while the keyboard is actually open.
 */
export function resolvePortalViewport(
  metrics: PortalViewportMetrics,
  floorToInnerHeight = false,
): { height: number; offsetTop: number; keyboardOpen: boolean } {
  const layoutHeight = Math.max(1, Math.round(metrics.layoutHeight));
  const visualHeight = Math.max(1, Math.round(metrics.visualHeight));
  const keyboardOpen =
    metrics.editableFocused &&
    Math.abs(metrics.scale - 1) < 0.01 &&
    Math.max(layoutHeight, metrics.innerHeight) - visualHeight > 120;
  const useLayout = metrics.standalone && !keyboardOpen;
  return {
    height: useLayout
      ? layoutHeight
      : Math.max(
          visualHeight,
          floorToInnerHeight && !keyboardOpen ? metrics.innerHeight : 1,
        ),
    offsetTop: useLayout ? 0 : Math.max(0, Math.round(metrics.visualTop)),
    keyboardOpen,
  };
}

/**
 * Prefer a real notch/island inset. A cached or iOS 26.1+ install can
 * report 0 or the compact 20px status bar while painting under the
 * Dynamic Island (WebKit #301994). iOS 26.5 also shrinks innerHeight /
 * 100vh / visualViewport.height by the island (screen 874, inner 812)
 * with offsetTop ≈ 0, so the layout origin is still the physical top
 * of the screen. Treating that short viewport as “OS already reserved
 * the bar” stamped 0 and left chrome in the island after #3119.
 *
 * Floor when the webview is edge-to-edge, when visualViewport omits a
 * large inset with offsetTop ≈ 0, when the missing height is itself
 * an island-sized band (44–80px) with offsetTop ≈ 0, or when a tall
 * island phone reports a nonzero inset below 44px. Skip in landscape
 * and on other platforms. A much shorter viewport (Safari toolbars) is
 * not an island lie.
 */
export function resolvePortalSafeTop({
  inset,
  maxInset = 0,
  standaloneIphone,
  layoutHeight,
  innerHeight = layoutHeight,
  visualHeight = layoutHeight,
  visualTop = 0,
  screenWidth,
  screenHeight,
}: {
  inset: number;
  maxInset?: number;
  standaloneIphone: boolean;
  layoutHeight: number;
  innerHeight?: number;
  visualHeight?: number;
  visualTop?: number;
  screenWidth: number;
  screenHeight: number;
}): number {
  const measured = Math.max(0, inset, maxInset);
  const portrait = screenWidth > 0 && screenHeight > screenWidth;
  const viewportH = Math.max(1, layoutHeight, innerHeight);
  const floor =
    portrait && screenHeight / screenWidth >= 2
      ? STANDALONE_SAFE_TOP_FLOOR_PX
      : COMPACT_STATUS_BAR_PX;

  if (measured >= REAL_NOTCH_MIN_PX) return measured;
  if (!standaloneIphone || !portrait) return measured;

  const fullBleed = viewportH >= screenHeight - 8;
  const landscapeViewport = viewportH <= screenWidth + 1;
  const vvOmitsInsets =
    visualTop <= 1 &&
    visualHeight > 0 &&
    visualHeight < viewportH - 50;
  // A nonzero inset under a real island (44px+) can only be the compact
  // status-bar value. Trusting it left mission-detail chrome ~20px down with
  // the island over the title whenever the viewport matched none of the
  // shape guesses below.
  const tallIslandPhone = screenHeight / screenWidth >= 2;
  const compactInsetLie = tallIslandPhone && measured > 0;
  const missingTop = screenHeight - viewportH;
  const ios26ViewportLie =
    visualTop <= 1 &&
    missingTop >= REAL_NOTCH_MIN_PX &&
    missingTop <= STANDALONE_SAFE_TOP_FLOOR_PX + IOS26_VIEWPORT_LIE_SLACK_PX;

  if (
    !landscapeViewport &&
    (fullBleed || vvOmitsInsets || ios26ViewportLie || compactInsetLie)
  ) {
    return Math.max(measured, floor);
  }
  return measured;
}
